/* eslint-disable react/prop-types */
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { hideMainNavigation } from "web/redux/ducks/main-navigation";
import { AppDispatch } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./main-navigation-desktop.module.css";

const CATEGORY_LIMIT = 12;

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
letters.push("0-9");

interface Menu {
    name: string;
    menus: Menu[];
    trend_menus: any[];
    display_name: string;
    links: MenuLink[];
    url?: string;
}

interface MenuLink {
    text: string;
    is_sale: boolean;
    url: string;
    is_trending: boolean;
}

interface MainNavigationDesktopProps {
    menuData: {
        menus: Menu[];
        links: MenuLink[];
    };
    featuredDesignersUrl: string;
    visible: boolean;
    dispatch: AppDispatch;
}

export function MainNavigationDesktop({
    menuData,
    featuredDesignersUrl,
    visible,
    dispatch,
}: MainNavigationDesktopProps): React.ReactElement {
    const types = menuData.menus;
    const links = menuData.links;
    const iconName = "arrow-icon";
    const [selectedType, setSelectedType] = useState(types[0]);
    const [selectedTrendMenu, setSelectedTrendMenu] = useState<any>(null);
    const [popularBrands, setPopularBrands] = useState<MenuLink[] | null>(null);
    const [expandedCategories, setExpandedCategories] = useState({});

    function selectType(type): void {
        setSelectedType(type);
        setSelectedTrendMenu(null);
        setPopularBrands(null);

        if (type.trend_menus && type.trend_menus.length === 1) {
            setSelectedTrendMenu(type.trend_menus[0]);
        }
    }

    function selectPopularBrands(type): void {
        setSelectedType(type);
        setSelectedTrendMenu(null);
        setPopularBrands(type.links);
    }

    useEffect(() => {
        setSelectedTrendMenu(null);
        setPopularBrands(null);

        if (types?.length && types[0]?.trend_menus?.length === 1) {
            setSelectedTrendMenu(types[0].trend_menus[0]);
        }
    }, [types]);

    function closeNavigation(): void {
        dispatch(hideMainNavigation());
    }

    function getInteraction(callback, label): { onClick: () => void } {
        return {
            onClick: (): void => {
                analytics.event("Menu click", "Clicked", label);
                callback();
            },
        };
    }

    function renderTypes(): React.ReactElement {
        return (
            <div className={styles.menuWrapper}>
                <ul className={styles.menu}>
                    {types.map((type) => {
                        const isPopularBrands = type.name === "popular_brands";

                        function interact(): void {
                            if (isPopularBrands) {
                                selectPopularBrands(type);
                            } else {
                                selectType(type);
                            }
                        }

                        const interaction = getInteraction(interact, "Category");
                        let displayText;

                        if (isPopularBrands) {
                            displayText = gettext("app_header_menu.brands_title");
                        }

                        if (!displayText) {
                            displayText = type.display_name;
                        }

                        return (
                            <li
                                key={type.display_name}
                                className={clsx(styles.menuItem, {
                                    [styles.activeMenuItem]: selectedType.name === type.name,
                                    [styles.clickable]: true,
                                })}
                                {...interaction}
                            >
                                {displayText}
                            </li>
                        );
                    })}
                    {links &&
                        links.map((link) => {
                            const isSaleLink = link.is_sale;
                            const isTrendingLink = link.is_trending;

                            return (
                                <li key={link.text} className={clsx(styles.menuItem)}>
                                    {isSaleLink && (
                                        <SVGIcon className={clsx(styles.saleTag)} name="tag" />
                                    )}
                                    <a
                                        href={link.url}
                                        className={clsx({
                                            [styles.saleLink]: isSaleLink,
                                            [styles.trendingLink]: isTrendingLink,
                                        })}
                                    >
                                        {link.text}
                                    </a>
                                </li>
                            );
                        })}
                </ul>
            </div>
        );
    }

    function renderCategories(): React.ReactElement {
        const totalItems = selectedType.links?.length;
        const hasHiddenItems = totalItems >= CATEGORY_LIMIT;
        const hideItems = hasHiddenItems && !expandedCategories[selectedType.name];

        return (
            <div className={clsx(styles.subMenu, { [styles.hideExtraLinks]: hideItems })}>
                <ul>
                    {Array.isArray(selectedType.links) &&
                        selectedType.links.map((link) => {
                            if (link.url === "") return;
                            return (
                                <li
                                    key={link.text}
                                    className={clsx(styles.subMenuItem, {
                                        [styles.saleMenuItem]: link.is_sale,
                                    })}
                                >
                                    {link.is_sale && (
                                        <SVGIcon className={styles.saleTag} name="tag" />
                                    )}
                                    <a href={link.url} className={styles.subMenuLink}>
                                        {link.text}
                                    </a>
                                </li>
                            );
                        })}
                </ul>

                {hideItems && (
                    <button
                        type="button"
                        className={styles.moreButton}
                        onClick={(): void =>
                            setExpandedCategories({
                                ...expandedCategories,
                                [selectedType.name]: true,
                            })
                        }
                    >
                        {"+ "}
                        {gettext("burger_menu.more_category_link")} {/* More */}
                    </button>
                )}
            </div>
        );
    }

    function renderTrends(): React.ReactElement | null {
        if (
            !selectedType.trend_menus ||
            !Array.isArray(selectedType.trend_menus) ||
            !selectedType.trend_menus.length
        ) {
            return null;
        }

        return (
            <div className={styles.subMenu}>
                <ul>
                    {selectedType.trend_menus.map((trendMenu) => {
                        function interact(): void {
                            setSelectedTrendMenu(trendMenu);
                        }
                        const interaction =
                            selectedType.trend_menus.length === 1
                                ? {}
                                : getInteraction(interact, "Trends");
                        return (
                            <li
                                key={trendMenu.display_name}
                                className={clsx(styles.subMenuItem, styles.trendItem, {
                                    [styles.subMenuItemActive]:
                                        selectedTrendMenu &&
                                        selectedTrendMenu.name === trendMenu.name,
                                    [styles.subMenuItemActiveBold]:
                                        selectedType.trend_menus.length === 1,
                                    [styles.clickable]: true,
                                })}
                                {...interaction}
                            >
                                {trendMenu.display_name}
                                {selectedType.trend_menus.length === 1 ? null : (
                                    <SVGIcon className={styles.subMenuChevron} name={iconName} />
                                )}
                            </li>
                        );
                    })}
                </ul>

                {/* If we have 1 trend menu only then render the links directly below */}
                {selectedType.trend_menus.length === 1 && (
                    <ul className={styles.featuredCategoriesList}>
                        {selectedType.trend_menus[0].links.map((link) => {
                            return (
                                <li
                                    key={link.url}
                                    className={clsx(styles.subMenuItem, {
                                        [styles.saleMenuItem]: link.is_sale,
                                    })}
                                >
                                    <a href={link.url} className={styles.subMenuLink}>
                                        {link.text}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    }

    function renderTrendLinks(): React.ReactElement | null {
        if (!selectedTrendMenu) {
            return null;
        }

        return (
            <ul className={styles.subMenu}>
                {selectedTrendMenu.links.map((link) => {
                    return (
                        <li
                            key={link.url}
                            className={clsx(styles.subMenuItem, {
                                [styles.saleMenuItem]: link.is_sale,
                            })}
                        >
                            <a href={link.url} className={styles.subMenuLink}>
                                {link.text}
                            </a>
                        </li>
                    );
                })}
            </ul>
        );
    }

    function renderPopularBrands(): React.ReactElement {
        return (
            <div className={styles.subMenusContainer}>
                <div className={styles.popularBrands}>
                    <span className={styles.subMenuTitle}>
                        {gettext("app_header_menu.popular_brands")}
                    </span>
                    <ul className={styles.popularBrandsList}>
                        {popularBrands &&
                            popularBrands.map((link) => (
                                <li
                                    key={link.url}
                                    className={clsx(styles.subMenuItem, styles.popularBrandItem)}
                                >
                                    <a href={link.url} className={clsx(styles.subMenuLink)}>
                                        {link.text}
                                    </a>
                                </li>
                            ))}
                    </ul>
                </div>
                <div>
                    <span className={styles.subMenuTitle}>
                        {gettext("app_header_menu.brands_a_z")}
                    </span>
                    <div className={styles.brandLetters}>
                        {letters.map((letter) => (
                            <a
                                key={`brand-initial-${letter}`}
                                href={`${featuredDesignersUrl}#${letter}`}
                                className={styles.brandLetter}
                            >
                                {letter}
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (visible) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }
    });

    return (
        <>
            <div
                className={clsx(styles.mainNavigationOverlay, {
                    [styles.show]: visible,
                })}
                onClick={(): void => {
                    dispatch(hideMainNavigation());
                }}
            />
            <div
                className={clsx(styles.mainNavigationDesktop, {
                    [styles.show]: visible,
                })}
            >
                {renderTypes()}
                <div className={styles.mainNavigationDesktopInner}>
                    <CloseButton className={styles.closeButton} onClick={closeNavigation} />
                    {popularBrands ? (
                        renderPopularBrands()
                    ) : (
                        <div className={styles.subMenusContainer}>
                            {renderCategories()}
                            {renderTrends()}
                            {/* If we have more than 1 trend menu we will render a third column when a trend menu is selected */}
                            {selectedType.trend_menus &&
                                selectedType.trend_menus.length > 1 &&
                                renderTrendLinks()}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default MainNavigationDesktop;
